import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Accordion, Alert, Button, ButtonGroup, Form, Table } from 'react-bootstrap';
import {
	createRetest,
	getEligibleRetestFindings,
} from '../Services/PtaasApiService';
import {
	CreateRetestPayload,
	RetestDisposition,
	RetestPhaseFindings,
} from '../Models/Retest';
import { LoaderIcon, ShieldIcon } from '../Components/Global/Icons';
import { FadeAccordion, FadeAccordionItem, FadeModal } from '../Components/Global/Transitions';
import RatingProps from '../Utilities/RatingProps';
import RiskAcceptedFindings from '../Components/Tables/RiskAcceptedFindings';
import RetestCompletionDate from '../Components/Popups/Projects/RetestCompletionDate';

interface Decision {
	disposition: RetestDisposition;
	reason: string;
}

export default function ProjectRetestPage() {
	const { id, canopyid } = useParams();
	const navigate = useNavigate();
	const { instance, accounts } = useMsal();

	// Where every exit from this page leads once the request is in: the retest table, which is
	// where the request the client just made becomes visible. The project overview no longer
	// carries retests at all, so landing there would give no sign it worked. The Canopy id is
	// carried back out whenever the route arrived with one.
	const retestsPath = `/projects/${id}${canopyid ? `/${canopyid}` : ''}/retests`;

	const [phases, setPhases] = useState<RetestPhaseFindings[]>([]);
	const [decisions, setDecisions] = useState<Record<string, Decision>>({});
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// A retest is only created once the client says when it has to be finished by, so the request
	// pauses here for the date. Risk-acceptance-only submissions never open this.
	const [askingCompletionDate, setAskingCompletionDate] = useState(false);
	// Populated only when the backend reports something the client must act on manually (e.g.
	// the Jira subtask failed to create). Non-empty means we hold on the confirmation screen
	// instead of silently navigating away, so the client actually sees it before it's gone.
	const [pendingActions, setPendingActions] = useState<string[]>([]);

	// Hoisted so RiskAcceptedFindings' onUndone can reuse it: undoing a risk accept
	// returns the finding to the eligible pool, which must reappear without a manual reload.
	const fetchPool = useCallback(() => {
		if (!id || accounts.length === 0) return;

		return getEligibleRetestFindings(instance, accounts[0], id)
			.then((result) => {
				setPhases(result);
			})
			// getEligibleRetestFindings throws `new Error(await response.text())`, so an empty body
			// yields an empty message. Falling back keeps `error` truthy — otherwise an empty-body
			// failure renders the same blank as a genuinely empty pool (see the error/phases gate below).
			.catch((e) => setError((e as Error).message || 'The findings awaiting retest could not be loaded. Please try again.'))
			.finally(() => setLoading(false));
	}, [id, instance, accounts]);

	useEffect(() => {
		fetchPool();
	}, [fetchPool]);

	const allFindings = useMemo(() => phases.flatMap((p) => p.findings), [phases]);

	// Only the findings given a disposition are part of the retest. Anything left undecided is
	// excluded and keeps its Canopy status, so it stays in the pool for a later retest.
	const decidedFindings = useMemo(
		() => allFindings.filter((f) => decisions[f.findingUuid]),
		[allFindings, decisions]
	);

	// At least one finding must be chosen, and every risk-accept needs a reason.
	// The server enforces both independently — the admin portal calls the same endpoint.
	const canSubmit = useMemo(
		() =>
			decidedFindings.length > 0 &&
			decidedFindings.every((f) => {
				const decision = decisions[f.findingUuid];
				if (decision.disposition === 'RiskAccepted') return decision.reason.trim().length > 0;
				return true;
			}),
		[decidedFindings, decisions]
	);

	const decide = (uuid: string, disposition: RetestDisposition) =>
		setDecisions((prev) => {
			// Clicking the disposition that is already active clears it, so a finding can be
			// returned to undecided without reloading the page.
			if (prev[uuid]?.disposition === disposition) {
				const next = { ...prev };
				delete next[uuid];
				return next;
			}
			return { ...prev, [uuid]: { disposition, reason: prev[uuid]?.reason ?? '' } };
		});

	const setReason = (uuid: string, reason: string) =>
		setDecisions((prev) => ({
			...prev,
			[uuid]: { disposition: prev[uuid]?.disposition ?? 'RiskAccepted', reason },
		}));

	const phaseAllRetest = (phase: RetestPhaseFindings) =>
		phase.findings.length > 0 &&
		phase.findings.every((f) => decisions[f.findingUuid]?.disposition === 'Retest');

	// One button for both directions: set the whole phase to Retest, or — once it already is —
	// clear every disposition in it so the phase can be undone in a single click.
	const toggleRetestAllInPhase = (phase: RetestPhaseFindings) =>
		setDecisions((prev) => {
			const clear = phase.findings.every((f) => prev[f.findingUuid]?.disposition === 'Retest');
			const next = { ...prev };
			phase.findings.forEach((f) => {
				if (clear) delete next[f.findingUuid];
				else next[f.findingUuid] = { disposition: 'Retest', reason: '' };
			});
			return next;
		});

	const submit = async (requestedCompletionDate?: string) => {
		if (!id) return;
		setSubmitting(true);
		setError(null);

		const payload: CreateRetestPayload = {
			// Only phases something was actually chosen from: the retest does not cover the rest.
			phaseScope: phases
				.filter((p) => p.findings.some((f) => decisions[f.findingUuid]))
				.map((p) => p.phaseId),
			requestedCompletionDate,
			findings: decidedFindings.map((f) => ({
				findingUuid: f.findingUuid,
				disposition: decisions[f.findingUuid].disposition,
				reason:
					decisions[f.findingUuid].disposition === 'RiskAccepted'
						? decisions[f.findingUuid].reason
						: undefined,
			})),
		};

		try {
			const result = await createRetest(instance, accounts[0], id, payload);
			// A non-empty pendingActions means something (e.g. the Jira subtask) needs manual
			// follow-up. Hold here and show it instead of navigating straight to a retest table
			// that looks perfectly healthy — the client is the only one who will ever see this.
			if (result.pendingActions.length > 0) {
				setPendingActions(result.pendingActions);
			} else {
				navigate(retestsPath);
			}
		} catch (e) {
			// createRetest throws `new Error(await response.text())`, so an empty body yields an
			// empty message. Falling back keeps `error` truthy — otherwise the alert renders
			// nothing and the button re-enables, and the client sees the submit do nothing.
			setError((e as Error).message || 'The retest request could not be submitted. Please try again.');
		} finally {
			setSubmitting(false);
		}
	};

	// LoaderIcon, not a Bootstrap Spinner: every other page in the app waits on this one, and a
	// page that spins differently reads as a different app.
	if (loading) return <LoaderIcon />;

	if (pendingActions.length > 0)
		return (
			<div>
				<Alert variant='warning'>
					<p>The retest was created, but needs manual follow-up:</p>
					<ul className='mb-0'>
						{pendingActions.map((action) => (
							<li key={action}>{action}</li>
						))}
					</ul>
				</Alert>
				<Button onClick={() => navigate(retestsPath)}>Continue</Button>
			</div>
		);

	// An unreachable Canopy and an empty pool must not look the same.
	if (error && phases.length === 0)
		return <Alert variant='danger'>{error}</Alert>;

	if (phases.length === 0)
		return (
			<div>
				<Alert variant='info'>
					There are no findings awaiting retest on this project.
				</Alert>
				{/* A finding that was risk accepted is excluded from the eligible pool above by
				    design (IsInPool), so this can be the only thing on the page — it must still
				    render here, not just when the pool is non-empty. */}
				{id && <RiskAcceptedFindings projectId={id} onUndone={fetchPool} />}
			</div>
		);

	const counts = {
		retest: allFindings.filter((f) => decisions[f.findingUuid]?.disposition === 'Retest').length,
		accepted: allFindings.filter((f) => decisions[f.findingUuid]?.disposition === 'RiskAccepted').length,
	};

	// With nothing to retest there is no tester work, no quote and no Jira subtask — the server
	// records the risk accepts and lands the retest Complete straight away. Calling that a
	// "retest request" would describe an action that never happens.
	const riskAcceptOnly = counts.retest === 0 && counts.accepted > 0;
	const submitLabel = riskAcceptOnly ? 'Submit Risk Acceptance' : 'Submit Retest Request';

	return (
		<div>
			<h4>Request Retest</h4>

			{error && <Alert variant='danger'>{error}</Alert>}

			{/* Every phase starts expanded. `alwaysOpen` alone only permits more than one panel to
			    be open — it opens none of them — so each phase's key has to be listed. Safe to
			    build from `phases` here: the loading branch above returns before this renders, so
			    the accordion first mounts with the phases already in hand. */}
			<FadeAccordion
				alwaysOpen
				defaultActiveKey={phases.map((p) => String(p.phaseId))}
				className='retest-accordion'
			>
				{phases.map((phase) => (
					<FadeAccordionItem key={phase.phaseId} eventKey={String(phase.phaseId)}>
						{/* as='h3' matches the Prerequisites accordion header. It renders an <h3>, not a
						    <button>, so the Retest All <button> can nest inside; its click stopPropagation
						    so it doesn't also toggle the panel. */}
						<Accordion.Button as='h3'>
							{`${phase.phaseTitle} (${phase.findings.length})`}
							<Button
								variant='outline-primary link'
								size='sm'
								className='ms-auto me-3'
								onClick={(e) => {
									e.stopPropagation();
									toggleRetestAllInPhase(phase);
								}}
							>
								{phaseAllRetest(phase) ? 'Clear All' : 'Retest All'}
							</Button>
						</Accordion.Button>
						<Accordion.Body>
							<Table hover size='sm'>
								<thead>
									<tr>
										<th>Severity</th>
										<th>Finding</th>
										<th id='canopy retest status'>Retest status</th>
										<th>Disposition</th>
									</tr>
								</thead>
								<tbody>
									{phase.findings.map((finding) => {
										const decision = decisions[finding.findingUuid];
										const { name, color } = RatingProps[finding.rating as keyof typeof RatingProps] ?? RatingProps[0];
										return (
											<tr key={finding.findingUuid}>
												<td><ShieldIcon className={'me-2 ' + color} />{name}</td>
												<td>
													{finding.title}
													{decision?.disposition === 'RiskAccepted' && (
														<Form.Control
															className='mt-1'
															size='sm'
															placeholder='Reason for risk acceptance (required)'
															value={decision.reason}
															onChange={(e) => setReason(finding.findingUuid, e.target.value)}
															isInvalid={decision.reason.trim().length === 0}
														/>
													)}
												</td>
												<td>{finding.retestStatus}</td>
												<td>
													{/* A segmented control: the two outcomes are mutually exclusive, so they read as
													    one control rather than two loose buttons. aria-pressed exposes the selected
													    state to assistive tech rather than only painting it on. */}
													<ButtonGroup size='sm'>
														<Button
															variant={decision?.disposition === 'Retest' ? 'primary' : 'outline-secondary'}
															aria-pressed={decision?.disposition === 'Retest'}
															onClick={() => decide(finding.findingUuid, 'Retest')}
														>
															Retest
														</Button>
														<Button
															variant={decision?.disposition === 'RiskAccepted' ? 'primary' : 'outline-secondary'}
															aria-pressed={decision?.disposition === 'RiskAccepted'}
															onClick={() => decide(finding.findingUuid, 'RiskAccepted')}
														>
															Risk Accept
														</Button>
													</ButtonGroup>
												</td>
											</tr>
										);
									})}
								</tbody>
							</Table>
						</Accordion.Body>
					</FadeAccordionItem>
				))}
			</FadeAccordion>

			{id && <RiskAcceptedFindings projectId={id} onUndone={fetchPool} />}

			<div className='d-flex justify-content-between align-items-center border-top pt-2'>
				<span className='text-muted small'>
					{counts.retest} to retest &middot; {counts.accepted} risk accepted &middot;{' '}
					{allFindings.length - counts.retest - counts.accepted} not included
				</span>
				{/* A risk acceptance is recorded and completed on the spot, so it goes straight out.
				    Anything with tester work in it stops for a completion date first. */}
				<Button
					disabled={!canSubmit || submitting}
					onClick={() => (riskAcceptOnly ? submit() : setAskingCompletionDate(true))}
				>
					{submitting ? 'Submitting…' : submitLabel}
				</Button>
			</div>

			{/* The popup closes as the request goes out, so a server failure lands in the alert at
			    the top of this page rather than behind a modal the client has to dismiss first. */}
			<FadeModal
				show={askingCompletionDate}
				onHide={() => setAskingCompletionDate(false)}
				title='Retest Completion Date'
			>
				<RetestCompletionDate
					close={() => setAskingCompletionDate(false)}
					confirm={(requestedCompletionDate) => {
						setAskingCompletionDate(false);
						submit(requestedCompletionDate);
					}}
				/>
			</FadeModal>
		</div>
	);
}
